import { Component, ElementRef, HostListener, Input, forwardRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconsModule } from '../../../icons/icons.module';

interface Option {
  value: string;
  label: string;
}

@Component({
  selector: 'zikatagov-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, IconsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZikataMultiSelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="multi-select-container" [class.disabled]="disabled">
      <div 
        class="select-input border-magenta" 
        (click)="toggleDropdown()"
        [class.focused]="isOpen"
        [class.disabled]="disabled"
      >
        <div class="selected-items border-magenta" [class.has-items]="selectedItems.length > 0">
          <div *ngIf="selectedItems.length === 0" >
            {{ placeholder }}
          </div>
          <div *ngFor="let item of selectedItems" class="selected-tag">
            {{ item.label }}
            <button 
              type="button"
              class="remove-tag" 
              (click)="removeItem(item, $event)"
              [disabled]="disabled"
            >
              <ng-icon 
                name="saxCloseCircleOutline" 
                class="icon-sm text-gray-500"
                size="16"
              ></ng-icon>
            </button>
          </div>
        </div>
        <div class="dropdown-icon">
          <ng-icon 
            *ngIf="!isOpen"
            name="saxArrowDown1Outline" 
            class="icon-sm text-gray-500"
            size="16"
          ></ng-icon>
          <ng-icon 
            *ngIf="isOpen"
            name="saxArrowUp2Outline" 
            class="icon-sm text-gray-500"
            size="16"
          ></ng-icon>
        </div>
      </div>

      <input
        #searchInput
        type="text"
        class="search-input border-magenta"
        [(ngModel)]="searchText"
        (input)="onSearch()"
        [class.visible]="isOpen"
        [placeholder]="searchPlaceholder"
        (click)="$event.stopPropagation()"
      >

      <div class="dropdown-menu" [class.show]="isOpen">
        <div *ngIf="filteredOptions.length === 0" class="no-results">
          No results found
        </div>
        <div 
          *ngFor="let option of filteredOptions" 
          class="dropdown-item"
          [class.selected]="isSelected(option)"
          (click)="toggleSelection(option)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
  `,
  styles: [`
    .multi-select-container {
      position: relative;
      width: 100%;
      font-family: inherit;
    }

    .select-input {
      min-height: 40px;
      padding: 4px 8px;
      border: 1px solid #D9D9D9;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      background: white;
      transition: all 0.2s;
      color: #2E005D;
    }

    .select-input:hover:not(.disabled) {
      border-color: #7323A8;
    }

    .select-input.focused {
      border-color: #7323A8;
      box-shadow: 0 0 0 2px rgba(115, 35, 168, 0.2);
    }

    .select-input.disabled {
      background: #F8F8F8;
      cursor: not-allowed;
    }

    .selected-items {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      flex: 1;
      min-height: 24px;
      color: #2E005D;
    }

    .selected-items.has-items {
      padding: 2px 0;
    }

    .selected-tag {
      background: #F8F8F8;
      padding: 2px 8px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.875rem;
      border: 1px solid #D9D9D9;
      color: #2E005D;
    }

    .remove-tag {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      color: #6C757D;
    }

    .remove-tag:hover:not(:disabled) {
      color: #E50085;
    }

    .remove-tag:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .search-input {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      padding: 8px;
      border-top: none;
      border-radius: 0 0 4px 4px;
      width: 100%;
      display: none;
      z-index: 1000;
      background: white;
      color: #2E005D;
    }

    .search-input.visible {
      display: block;
    }

    .search-input::placeholder {
      color: #6C757D;
    }

    .dropdown-menu {
      position: absolute;
      top: calc(100% + 36px);
      left: 0;
      right: 0;
      max-height: 200px;
      overflow-y: auto;
      background: white;
      border: 1px solid #D9D9D9;
      border-top: none;
      border-radius: 0 0 4px 4px;
      display: none;
      z-index: 1000;
    }

    .dropdown-menu.show {
      display: block;
    }

    .dropdown-item {
      padding: 8px;
      cursor: pointer;
      transition: all 0.2s;
      color: #2E005D;
    }

    .dropdown-item:hover {
      background: #F8F8F8;
    }

    .dropdown-item.selected {
      background: #FFE5DD;
      color: #2E005D;
    }

    .no-results {
      padding: 8px;
      color: #6C757D;
      text-align: center;
    }

    .icon-sm {
      display: flex;
      align-items: center;
      color: #6C757D;
    }
  `]
})
export class ZikataMultiSelectComponent implements ControlValueAccessor {
  @Input() id = '';
  @Input() disabled = false;
  @Input() options: Option[] = [];
  @Input() placeholder = 'Select items...';
  @Input() searchPlaceholder = 'Search...';

  @ViewChild('searchInput') searchInput!: ElementRef;

  isOpen = false;
  searchText = '';
  selectedItems: Option[] = [];
  filteredOptions: Option[] = [];
  onChange: any = () => {};
  onTouch: any = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!(event.target as HTMLElement).closest('.multi-select-container')) {
      this.isOpen = false;
      this.searchText = '';
      this.filterOptions();
    }
  }

  ngOnInit() {
    this.filteredOptions = [...this.options];
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        });
      } else {
        this.searchText = '';
        this.filterOptions();
      }
    }
  }

  onSearch() {
    this.filterOptions();
  }

  filterOptions() {
    if (!this.searchText) {
      this.filteredOptions = [...this.options];
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredOptions = this.options.filter(option =>
        option.label.toLowerCase().includes(searchLower)
      );
    }
  }

  toggleSelection(option: Option) {
    const index = this.selectedItems.findIndex(item => item.value === option.value);
    
    if (index === -1) {
      this.selectedItems = [...this.selectedItems, option];
    } else {
      this.selectedItems = this.selectedItems.filter(item => item.value !== option.value);
    }

    this.updateValue();
  }

  removeItem(item: Option, event: Event) {
    event.stopPropagation();
    if (!this.disabled) {
      this.selectedItems = this.selectedItems.filter(i => i.value !== item.value);
      this.updateValue();
    }
  }

  isSelected(option: Option): boolean {
    return this.selectedItems.some(item => item.value === option.value);
  }

  private updateValue() {
    const values = this.selectedItems.map(item => item.value);
    this.onChange(values);
    this.onTouch();
  }

  // ControlValueAccessor implementation
  writeValue(values: string[]): void {
    if (values) {
      this.selectedItems = this.options.filter(option => 
        values.includes(option.value)
      );
    } else {
      this.selectedItems = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}